import { useEffect, useMemo, useRef, useState } from "react";
import {
    fetchDiagnosis,
    fetchModifiers,
    fetchPlaceOfService,
    fetchProcedureCodes,
    fetchProviderTaxonomy,
    fetchServices,
    fetchTypeOfBill,
} from "../../utils/apiUtils";
import { loadServices } from "./serviceCatalog.js";

// Used when the Codes API can't be reached, so suggestions still work offline and in local dev.
const COMMON = {
    pos: [
        ["02", "Telehealth other than home"], ["10", "Telehealth in the patient's home"], ["11", "Office"], ["12", "Home"],
        ["19", "Off-campus outpatient hospital"], ["20", "Urgent care facility"], ["21", "Inpatient hospital"],
        ["22", "On-campus outpatient hospital"], ["23", "Emergency room, hospital"], ["24", "Ambulatory surgical center"],
        ["31", "Skilled nursing facility"], ["41", "Ambulance, land"], ["42", "Ambulance, air or water"],
        ["49", "Independent clinic"], ["50", "Federally qualified health center"], ["65", "End-stage renal disease facility"],
        ["81", "Independent laboratory"],
    ],
    tob: [
        ["0111", "Hospital inpatient, admit through discharge"], ["0121", "Hospital inpatient, Part B only"],
        ["0131", "Hospital outpatient"], ["0141", "Hospital, other (not registered)"], ["0211", "Skilled nursing, inpatient"],
        ["0711", "Rural health clinic"], ["0731", "Federally qualified health center"], ["0761", "Community mental health center"],
        ["0831", "Ambulatory surgery center"], ["0851", "Critical access hospital"],
    ],
    modifier: [
        ["25", "Significant, separate E/M service the same day"], ["26", "Professional component"], ["50", "Bilateral procedure"],
        ["51", "Multiple procedures"], ["59", "Distinct procedural service"], ["76", "Repeat procedure, same physician"],
        ["95", "Synchronous telemedicine"], ["GT", "Interactive audio and video telecommunication"], ["LT", "Left side"],
        ["RT", "Right side"], ["TC", "Technical component"], ["XU", "Unusual non-overlapping service"],
    ],
    taxonomy: [
        ["207Q00000X", "Family medicine"], ["207R00000X", "Internal medicine"], ["208000000X", "Pediatrics"],
        ["208D00000X", "General practice"], ["363L00000X", "Nurse practitioner"], ["363A00000X", "Physician assistant"],
        ["207V00000X", "Obstetrics and gynecology"], ["207RC0000X", "Cardiovascular disease"], ["207P00000X", "Emergency medicine"],
        ["2085R0202X", "Diagnostic radiology"], ["225100000X", "Physical therapist"], ["111N00000X", "Chiropractor"],
        ["171100000X", "Acupuncturist"], ["103T00000X", "Psychologist"], ["282N00000X", "General acute care hospital"],
        ["261QA1903X", "Ambulatory surgical clinic"], ["291U00000X", "Clinical medical laboratory"], ["3416L0300X", "Land ambulance"],
    ],
};

const CATALOG_FIELDS = {
    cpt: ["cptCodes"],
    hcpcs: ["hcpcsCodes"],
    revenue: ["revenueCode"],
    pos: ["placesOfService"],
    tob: ["typeOfBill"],
    taxonomy: ["taxonomyCodes"],
};

const serviceRow = (row) => ({ code: row.serviceCode, description: row.serviceShortDesc || row.serviceLongDesc });

export const CODE_KINDS = {
    cpt: { path: "services", name: "CPT", search: (text) => fetchServices({ searchText: text, limit: 8, serviceTypeName: "CPT-4 code" }), row: serviceRow },
    hcpcs: { path: "services", name: "HCPCS", search: (text) => fetchServices({ searchText: text, limit: 8, serviceTypeName: "HCPCS code" }), row: serviceRow },
    revenue: { path: "revenue", name: "revenue", search: (text) => fetchServices({ searchText: text, limit: 8, serviceTypeName: "UB Revenue Code" }), row: serviceRow },
    drug: { path: "drugs", name: "drug", search: (text) => fetchServices({ searchText: text, limit: 8, serviceTypeName: "NDC 5-4-2" }), row: serviceRow },
    pos: { path: "place-of-service", name: "place of service", search: (text) => fetchPlaceOfService({ searchText: text, limit: 8 }), row: (row) => ({ code: row.placeOfServiceCode, description: row.placeOfServiceDesc }) },
    tob: {
        path: "type-of-bill",
        name: "type of bill",
        query: (code) => code.replace(/^0(?=\d{3}$)/, ""),
        search: (text) => fetchTypeOfBill({ searchText: text.replace(/^0(?=\d{2,3}$)/, ""), limit: 8 }),
        row: (row) => ({ code: String(row.typeOfBillCode || "").padStart(4, "0"), description: row.typeOfBillDesc }),
    },
    modifier: { path: "modifiers", name: "modifier", search: (text) => fetchModifiers({ searchText: text, limit: 8 }), row: (row) => ({ code: row.modifierCode, description: row.modifierName || row.modifierDesc }) },
    procedure: { path: "procedures", name: "procedure", search: (text) => fetchProcedureCodes({ searchText: text, limit: 8 }), row: (row) => ({ code: row.procedureCode, description: row.procedureShortDesc }) },
    taxonomy: { path: "taxonomy", name: "taxonomy", search: (text) => fetchProviderTaxonomy({ searchText: text, limit: 8 }), row: (row) => ({ code: row.providerTaxonomyCode, description: row.providerTaxonomyName || row.classification }) },
    diagnosis: { path: "diagnosis", name: "diagnosis", search: (text) => fetchDiagnosis({ searchText: text, limit: 8 }), row: (row) => ({ code: row.diagnosisCode, description: row.diagnosisShortDesc }) },
};

export const codeHref = (kind, code) => {
    const entry = CODE_KINDS[kind];
    const search = entry?.query ? entry.query(code) : code;
    return `/codes/${entry?.path || "services"}?search=${encodeURIComponent(search)}`;
};

const splitCodes = (text) => String(text || "").split(",").map((item) => item.trim()).filter(Boolean);

const localCodes = (kind) => {
    const known = new Map((COMMON[kind] || []).map(([code, description]) => [code, description]));
    (CATALOG_FIELDS[kind] || []).forEach((field) => {
        loadServices().forEach((service) => (service[field] || []).forEach((code) => {
            if (!known.has(code)) known.set(code, `Used by ${service.key.replace(/([A-Z])/g, " $1").toLowerCase()}`);
        }));
    });
    return [...known].map(([code, description]) => ({ code, description }));
};

export const CodeLink = ({ kind, code }) => (
    <a
        className="internal-work__code-link"
        href={codeHref(kind, code)}
        target="_blank"
        rel="noopener noreferrer"
        title={`Open ${code} on the Codes page`}
    >
        {code}
    </a>
);

// A comma-separated code list with suggestions for the code being typed, and links to the Codes page.
export const CodeListField = ({ label, value, onChange, kind, placeholder, readOnly }) => {
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(0);
    const [remote, setRemote] = useState([]);
    const inputRef = useRef(null);
    const codes = splitCodes(value);
    const text = String(value || "");
    const token = text.slice(text.lastIndexOf(",") + 1).trim();
    const local = useMemo(() => localCodes(kind), [kind]);

    useEffect(() => {
        if (!open || !token || readOnly) {
            setRemote([]);
            return undefined;
        }
        let current = true;
        const timer = window.setTimeout(async () => {
            try {
                const result = await CODE_KINDS[kind].search(token);
                if (current) setRemote((result?.rows || []).map(CODE_KINDS[kind].row).filter((row) => row.code));
            } catch {
                if (current) setRemote([]);
            }
        }, 250);
        return () => {
            current = false;
            window.clearTimeout(timer);
        };
    }, [kind, open, readOnly, token]);

    const suggestions = useMemo(() => {
        const taken = new Set(codes.slice(0, token ? -1 : undefined));
        const needle = token.toLowerCase();
        const matches = (item) => !needle || item.code.toLowerCase().startsWith(needle) || String(item.description || "").toLowerCase().includes(needle);
        const seen = new Set();
        return [...remote, ...local.filter(matches)]
            .filter((item) => !taken.has(item.code) && !seen.has(item.code) && seen.add(item.code))
            .slice(0, 8);
    }, [codes, local, remote, token]);

    const pick = (code) => {
        const kept = token ? codes.slice(0, -1) : codes;
        onChange(`${[...kept, code].join(", ")}, `);
        setActive(0);
        setOpen(false);
        inputRef.current?.focus();
    };

    const onKeyDown = (event) => {
        if (!open || !token || !suggestions.length) return;
        if (event.key === "ArrowDown") {
            event.preventDefault();
            setActive((index) => (index + 1) % suggestions.length);
        } else if (event.key === "ArrowUp") {
            event.preventDefault();
            setActive((index) => (index - 1 + suggestions.length) % suggestions.length);
        } else if (event.key === "Enter" && token) {
            event.preventDefault();
            pick(suggestions[active].code);
        } else if (event.key === "Escape") {
            event.stopPropagation();
            setOpen(false);
        }
    };

    return (
        <div className="internal-work__field internal-work__code-field">
            <span>{label}</span>
            <div className="internal-work__code-input">
                <input
                    ref={inputRef}
                    type="text"
                    value={text}
                    placeholder={placeholder}
                    readOnly={readOnly}
                    autoComplete="off"
                    role="combobox"
                    aria-expanded={open && Boolean(token) && suggestions.length > 0}
                    aria-label={label}
                    onFocus={() => setOpen(true)}
                    onBlur={() => setOpen(false)}
                    onKeyDown={onKeyDown}
                    onChange={(event) => {
                        onChange(event.target.value);
                        setActive(0);
                        setOpen(true);
                    }}
                />
                {open && token && !readOnly && suggestions.length ? (
                    <ul className="internal-work__code-menu" role="listbox">
                        {suggestions.map((item, index) => (
                            <li
                                key={item.code}
                                role="option"
                                aria-selected={index === active}
                                className={index === active ? "is-active" : undefined}
                                onMouseEnter={() => setActive(index)}
                                onMouseDown={(event) => {
                                    event.preventDefault();
                                    pick(item.code);
                                }}
                            >
                                <b>{item.code}</b>
                                {item.description ? <small>{item.description}</small> : null}
                            </li>
                        ))}
                    </ul>
                ) : null}
            </div>
            {codes.length ? (
                <div className="internal-work__code-links" aria-label={`Look up ${label}`}>
                    {codes.map((code) => <CodeLink key={code} kind={kind} code={code} />)}
                </div>
            ) : null}
        </div>
    );
};
